<template>
    <div class="assistent-personality-form">
        <flex-container
            size="full"
            height="full"
        >
            <alert-block v-if="hasCustomInstruction" :color="'blue-500'">
                <template #prefix>
                    <svg-icon :icon="WarningIcon" />
                </template>

                Sommige persoonlijkheidsinstellingen kunnen hier niet worden aangepast omdat de AI Agent eigen instructies gebruikt.
                Herstel de standaard instructies om deze instellingen weer te kunnen wijzigen.
            </alert-block>

            <flex-container
                size="auto"
                direction="column"
                :gap="null"
            >
                <text-heading>Persoonlijkheid</text-heading>
                <text-description>Wijzig hier de persoonlijkheid van je persoonlijke AI Agent</text-description>
            </flex-container>

            <form-validation>
                <flex-container>
                    <input-group
                        v-model="assistent.name"
                        :schema="string().required()"
                    >
                        <template #input>
                            <flex-container
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Naam van de AI Agent
                                </text-decorator>
                                <text-input :disabled="hasCustomInstruction" />
                            </flex-container>
                        </template>
                    </input-group>
                    <input-group
                        v-model="assistent.voice"
                        :schema="Voice.validationSchema"
                    >
                        <template #input>
                            <flex-container
                                :gap="Size.xs"
                                style="position: relative"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Stem
                                    <svg-icon
                                        :icon="PlayPauseIcon"
                                        :size="IconSize.md"
                                        :color="'blue-500'"
                                        style="position: absolute; right: 10px; top: 25px"
                                    />
                                </text-decorator>
                                <select-input
                                    :items="Voice.values"
                                />
                            </flex-container>
                        </template>
                    </input-group>
                </flex-container>

                <flex-container :gap="Size.xs">
                    <flex-container>
                        <text-decorator :size="FontSize.xs">
                            Avatar
                        </text-decorator>
                    </flex-container>
                    <flex-container no-wrap>
                        <flex-container size="auto">
                            <input-group
                                v-model="assistent.avatarVariant"
                                :schema="AvatarVariant.validationSchema"
                            >
                                <template #input>
                                    <image-radio-group-input
                                        :items="avatarVariantItems"
                                        :images="avatarVariantImages"
                                        value-key-name="value"
                                        icon-key-name="image"
                                    />
                                </template>
                            </input-group>
                        </flex-container>

                        <flex-container style="min-width: auto">
                            <input-group v-model="avatar">
                                <template #input>
                                    <file-drop-zone-input
                                        direct-upload
                                        :direct-upload-callback="uploadAvatar"
                                        variant="row"
                                    >
                                        <flex-container
                                            direction="column"
                                            :gap="Size.xs"
                                        >
                                            <div>
                                                <text-decorator
                                                    :size="FontSize.xs"
                                                    bold
                                                >
                                                    Selecteer
                                                </text-decorator>
                                                <text-decorator
                                                    :size="FontSize.xs"
                                                    bold
                                                    :color="'primary'"
                                                >
                                                    een afbeelding
                                                </text-decorator>
                                            </div>
                                            <div>
                                                <text-decorator
                                                    :size="FontSize.xs"
                                                >
                                                    Ondersteund: JPG, PNG
                                                </text-decorator>
                                            </div>
                                        </flex-container>
                                    </file-drop-zone-input>
                                </template>
                            </input-group>
                        </flex-container>
                    </flex-container>
                </flex-container>
                <flex-container size="full" no-wrap>
                    <input-group
                        v-model="assistent.widget.color"
                        :schema="string().required()"
                    >
                        <template #input>
                            <flex-container
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Widget kleur
                                </text-decorator>
                                <color-input />
                            </flex-container>
                        </template>
                    </input-group>
                    <input-group
                        v-model="assistent.widget.balloonColor"
                        :schema="string().required()"
                    >
                        <template #input>
                            <flex-container
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Chatballon kleur
                                </text-decorator>
                                <color-input />
                            </flex-container>
                        </template>
                    </input-group>
                </flex-container>
                <input-group
                    v-model="assistent.behavior.personality"
                    :schema="Personality.validationSchema"
                >
                    <template #input>
                        <flex-container
                            direction="column"
                            :gap="Size.xs"
                        >
                            <text-decorator :size="FontSize.xs">
                                Persoonlijkheid
                            </text-decorator>
                            <select-input
                                :items="Personality.values"
                                :disabled="hasCustomInstruction"
                            />
                        </flex-container>
                    </template>
                </input-group>

                <flex-container size="full" no-wrap>
                    <input-group
                        v-model="assistent.behavior.formality"
                        :schema="Formality.validationSchema"
                    >
                        <template #input>
                            <flex-container
                                direction="column"
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Spreekvorm
                                </text-decorator>
                                <radio-group-input
                                    label-key-name="label"
                                    value-key-name="value"
                                    :items="Formality.values"
                                    :disabled="hasCustomInstruction"
                                />
                            </flex-container>
                        </template>
                    </input-group>
                    <input-group
                        v-model="assistent.behavior.allowEmoticons"
                        :schema="boolean().required()"
                    >
                        <template #input>
                            <flex-container
                                direction="column"
                                :gap="Size.xs"
                            >
                                <text-decorator :size="FontSize.xs">
                                    Gebruik van emoticon's/ emoji's
                                </text-decorator>
                                <switch-input
                                    :disabled="hasCustomInstruction"
                                />
                            </flex-container>
                        </template>
                    </input-group>
                </flex-container>
                <input-group
                    v-model="assistent.behavior.answerLength"
                    :schema="object().required()"
                >
                    <template #input>
                        <flex-container
                            direction="column"
                            :gap="Size.xs"
                        >
                            <text-decorator :size="FontSize.xs">
                                Antwoordlengte
                            </text-decorator>
                            <radio-group-input
                                label-key-name="label"
                                value-key-name="value"
                                :items="AnswerLength.values"
                                :disabled="hasCustomInstruction"
                            >
                                <template #default="{ bindings }">
                                    <flex-container
                                        v-tooltip="bindings.item.tooltip"
                                        :gap="Size.xs"
                                        align-items="center"
                                        inline
                                    >
                                        {{ bindings.item.label }}
                                        <svg-icon
                                            :color="'gray-500'"
                                            :icon="IconCircleInfo"
                                            :size="IconSize.xxs"
                                            is-custom-icon
                                        />
                                    </flex-container>
                                </template>
                            </radio-group-input>
                        </flex-container>
                    </template>
                </input-group>
                <input-group
                    v-model="assistent.widget.greeting"
                    :schema="string().required()"
                >
                    <template #input>
                        <flex-container
                            :gap="Size.xs"
                        >
                            <text-decorator :size="FontSize.xs">
                                Welkomsttekst
                            </text-decorator>
                            <text-area-input height="20vh" />
                        </flex-container>
                    </template>
                </input-group>
            </form-validation>
        </flex-container>
    </div>
</template>

<script setup lang="ts">
    import {
        ColorInput,
        FlexContainer,
        FontSize,
        FormValidation,
        IconSize,
        ImageRadioGroupInput,
        InputGroup,
        RadioGroupInput,
        SelectInput,
        Size,
        SvgIcon,
        SwitchInput,
        TextAreaInput,
        TextDecorator,
        TextDescription,
        TextHeading,
    } from '@keyvue/one-ui';

    import {
        AnswerLength,
        Assistent,
        Avatar,
        AvatarId,
        AvatarVariant,
        Formality,
        Personality,
        Voice,
    } from '@keyvue/one-api-client';
    import { computed, type Ref, ref, watch } from 'vue';
    import { boolean, object, string } from 'yup';
    import PlayPauseIcon from '@keyvue/one-material-icons/icons/play_arrow.svg?component';
    import IconCircleInfo from '@keyvue/one-awesome-icons/icons/regular/circle-info.svg?component';
    import WarningIcon from '@keyvue/one-material-icons/icons/warning.svg?component';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });

    const avatar = ref(null);

    const avatarVariantItems = computed(() => {
        let items = AvatarVariant.values;

        if (assistent.value.avatar === null) {
            items = items.filter((item) => !AvatarVariant.CUSTOM.equals(item));
        }

        return items;
    });

    const avatarVariantImages = computed(() => {
        const images = [
            '/avatars/1.png',
            '/avatars/2.png',
            '/avatars/3.png',
        ];

        if (assistent.value.avatar?.url) {
            images.push(assistent.value.avatar.url);
        }

        return images;
    });

    const hasCustomInstruction = computed(() => assistent.value.behavior.customInstructionEnabled);

    const uploadAvatar = async (files: File[]) => {
        const file = files.pop();
        if (!file) {
            return;
        }

        assistent.value.avatar = Avatar.fromObject({
            assistent: assistent.value,
            id: AvatarId.generate(),
            file,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            url: URL.createObjectURL(file),
        });

        assistent.value.avatarVariant = AvatarVariant.CUSTOM;
    };
</script>
